import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useToast } from "@/components/v2/Toast";
import { useCompleteOnboarding } from "@/lib/tenant-me";
import { useRegisterStep } from "./OnboardingShell";
import { StepShell, StepBody } from "./_StepShell";
import { StepHero } from "./_StepHero";
import { StepFooter } from "./_StepFooter";

const PRO_FEATURES = [
  "Unlimited services",
  "Unlimited public bookings",
  "Unlimited operators",
  "Manual receipt verification",
  "All features included",
];

export function PlanStep() {
  const complete = useCompleteOnboarding();
  const navigate = useNavigate();
  const toast = useToast();

  useRegisterStep({
    onContinue: async () => {
      try {
        await complete.mutateAsync();
        navigate("/onboarding/done", { replace: true });
      } catch (e) {
        toast.error("Couldn't finish setup", (e as Error).message);
      }
    },
    disabled: complete.isPending,
    continueLabel: complete.isPending ? "Finishing…" : "Let's go",
  });

  return (
    <StepShell variant="wide">
      <StepHero
        eyebrow="Step 5 of 5"
        title="You're on Pro"
        subtitle="Everything is on the Pro plan — and right now Pro is free for everyone. No billing, no card, no catch."
      />

      <StepBody>
        <div className="max-w-sm mx-auto">
          <div className="rounded-xl border border-primary ring-2 ring-primary/30 bg-primary-light/30 p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold tracking-tight text-lg">Pro plan</p>
              <span className="text-[10px] uppercase tracking-wider rounded-full bg-primary/10 text-primary px-2 py-0.5 font-medium">
                Free while we build
              </span>
            </div>
            <div>
              <p className="text-3xl font-bold tracking-tight">Free</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                No credit card · No trial clock · No catch
              </p>
            </div>
            <ul className="text-xs space-y-1.5">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-1.5">
                  <Check className="h-3.5 w-3.5 text-trend-positive shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </StepBody>

      <StepFooter />
    </StepShell>
  );
}
